/**
 * PD-17406 — a request the storefront could not send at all.
 *
 * When `fetch()` itself rejects, the browser never got an answer from our
 * servers: a firewall, security software, a browser extension or a dropped
 * connection stopped the request. Browsers describe that in their own words
 * ("Failed to fetch" in Chrome, "Load failed" in Safari, "NetworkError when
 * attempting to fetch resource." in Firefox), and the storefront used to show
 * that raw text in the sign-in dialog, which told the shopper nothing they
 * could act on. Every storefront request now goes through
 * `fetchOrNetworkError`, which turns that rejection into a PortalNetworkError
 * whose message is a plain sentence saying what happened and what to try.
 *
 * Kept in its own file because apiClient and portalTokenService both use it,
 * and portalTokenService cannot import apiClient (apiClient imports it).
 */
import type {
	PortalNetworkService,
	PortalSignInStep,
} from '@supply-mover-2-0/shared';

/**
 * Shown wherever a PortalNetworkError reaches the screen. It is the error's
 * message, so the screens that already show `t(error.message)` translate it
 * with no change of their own (t() is only available inside components).
 */
export const NETWORK_BLOCKED_MESSAGE =
	"We couldn't connect from this device. A firewall, security software or browser extension may be blocking the connection. Try another browser or network, or contact your IT team.";

/**
 * Deliberately has no `status`: nothing answered. Code that branches on the
 * HTTP status (session restore keeps the saved session unless the status is
 * 401 or 403, checkout treats a status-less failure as "the order may have
 * been placed") keeps treating it as the dropped connection it is.
 */
export class PortalNetworkError extends Error {
	/** Which of our servers the request was going to. */
	public readonly service: PortalNetworkService;

	/** The sign-in step that failed, when the caller knows it. */
	public readonly step?: PortalSignInStep;

	/**
	 * The browser's (or the native HTTP plugin's) original error. Set by hand
	 * because the storefront compiles against ES2020, which has no
	 * `Error(message, { cause })`.
	 */
	public readonly cause: unknown;

	public constructor(
		service: PortalNetworkService,
		cause: unknown,
		step?: PortalSignInStep,
	) {
		super(NETWORK_BLOCKED_MESSAGE);
		this.name = 'PortalNetworkError';
		this.service = service;
		this.step = step;
		this.cause = cause;
	}
}

export function isPortalNetworkError(
	error: unknown,
): error is PortalNetworkError {
	return error instanceof PortalNetworkError;
}

function isAbortError(error: unknown): boolean {
	return (
		typeof error === 'object' &&
		error != null &&
		(error as { name?: unknown }).name === 'AbortError'
	);
}

/**
 * `fetch()`, except that a request that could not be sent rejects with a
 * PortalNetworkError instead of the browser's own error.
 *
 * Any rejection counts, not just TypeError: the storefront mobile app sends
 * requests through CapacitorHttp, whose native failures arrive as a plain
 * Error. The one exception is an abort, which the caller asked for (a
 * navigation or an AbortController), so it is passed through unchanged.
 * HTTP error responses (401, 500, ...) resolve as before; only "no response"
 * is changed.
 */
export async function fetchOrNetworkError(
	input: string,
	init: RequestInit | undefined,
	service: PortalNetworkService,
	step?: PortalSignInStep,
): Promise<Response> {
	try {
		return await fetch(input, init);
	} catch (error: unknown) {
		if (isAbortError(error)) {
			throw error;
		}
		// The browser's own wording is still useful to a developer.
		console.debug('[PortalNetworkError] request could not be sent', error);
		throw new PortalNetworkError(service, error, step);
	}
}
